
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PropBotForm } from '@/components/admin/prop-trading/PropBotForm';
import { useAdminPropBotDetail, useUpdatePropBot } from '@/hooks/use-admin-prop-bots';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';

const PropBotEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { 
    data: propBot, 
    isLoading: isLoadingBot, 
    isError 
  } = useAdminPropBotDetail(id);
  
  const { 
    mutateAsync: updatePropBot, 
    isPending: isUpdating 
  } = useUpdatePropBot();

  const handleUpdatePropBot = async (data: any) => {
    try {
      if (id) {
        await updatePropBot({ botId: id, botData: data });
        // Navigate back to the prop bots list after successful update
        navigate('/admin/prop-trading');
      }
    } catch (error) {
      console.error('Error updating prop bot:', error);
      // Toast is handled in the mutation hook
    }
  };

  if (isLoadingBot) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading bot details...</p>
        </div>
      </div>
    );
  }

  if (isError || !propBot) {
    return (
      <div className="">
        <div className="bg-destructive/10 p-6 rounded-md">
          <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Bot</h2>
          <p className="text-muted-foreground">
            There was an error loading the prop bot. It may have been deleted or you may not have permission to view it.
          </p>
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/prop-trading')}
            className="mt-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/admin/prop-trading')}
            className="mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to List
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Edit Prop Trading Bot</h1>
          <p className="text-muted-foreground">
            Update details for {propBot.name}.
          </p>
        </div>
      </div>
      
      <PropBotForm 
        initialData={propBot} 
        onSubmit={handleUpdatePropBot} 
        isLoading={isUpdating}
        mode="edit"
      />
    </div>
  );
};

export default PropBotEditPage;
